/**
 * `mitigatesHazard` for one obligation (SPEC_Hazard_UI §5).
 *
 * Multi-select from the closed catalogue, never free text. Saved on its own
 * rather than through the obligation review action: the link is curation of the
 * hazard layer, not a change to the extracted legal statement, and mixing the
 * two would put an unrelated field into the obligation's review trail.
 *
 * The ceremony contribution is shown next to the selection because that is the
 * point of the field — it is the input the siBase derivation aggregates over.
 */

import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronsUpDown, Loader2 } from "lucide-react";

import Section from "../../components/Section";
import { useModalContext } from "../../components/ModalProvider";
import { useLegalAdministrationAPI } from "../../LegalAdministrationAPI";
import { HazardChip, damageTypeLabel } from "./hazardDisplay";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface Properties {
  obligationKey: string;
  mitigatesHazard?: string[] | null;
  onSaved?: () => void;
}

export default function ObligationHazardSection(properties: Properties) {
  const { t } = useTranslation();
  const { pushError, pushSuccess } = useModalContext() as any;
  const legalAPI = useLegalAdministrationAPI();
  const labelId = useId();

  const [catalog, setCatalog] = useState<any[]>([]);
  const [selected, setSelected] = useState<string[]>(properties.mitigatesHazard || []);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await legalAPI.getHazards();
        if (!cancelled) setCatalog(result?.hazards || []);
      } catch (error) {
        if (!cancelled) pushError(error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelected(properties.mitigatesHazard || []);
  }, [properties.mitigatesHazard, properties.obligationKey]);

  const dirty =
    [...selected].sort().join(",") !== [...(properties.mitigatesHazard || [])].sort().join(",");

  const save = async () => {
    try {
      setSaving(true);
      await legalAPI.saveObligationMitigatesHazard(properties.obligationKey, selected);
      pushSuccess(t("operatorResponsibilityPanel.hazardAssignmentSaved"));
      properties.onSaved?.();
    } catch (error) {
      pushError(error);
    } finally {
      setSaving(false);
    }
  };

  const byCode = new Map(catalog.map((hazard: any) => [hazard.code, hazard]));

  // Stillgelegte Gefährdungen bleiben wählbar, solange sie zugeordnet sind —
  // sonst verschwände eine bestehende Zuordnung lautlos aus der Auswahl.
  const options = catalog.filter(
    (hazard: any) => !hazard.deprecated || selected.includes(hazard.code),
  );

  const triggerText =
    selected.length === 0
      ? t("operatorResponsibilityPanel.mitigatesHazardPlaceholder")
      : selected
          .map((code) => `${code} · ${byCode.get(code)?.label || code}`)
          .join(", ");

  return (
    <Section title={t("operatorResponsibilityPanel.mitigatesHazard")}>
      <p className="min-w-0 break-words text-sm text-muted-foreground">
        {t("operatorResponsibilityPanel.mitigatesHazardHint")}
      </p>

      <div className="mt-4 flex min-w-0 flex-wrap items-end gap-4">
        <div className="flex min-w-[260px] flex-1 flex-col gap-1.5">
          <Label id={labelId}>{t("operatorResponsibilityPanel.mitigatesHazard")}</Label>
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-labelledby={labelId}
                aria-expanded={pickerOpen}
                className="w-full justify-between font-normal"
              >
                <span className="truncate">{triggerText}</span>
                <ChevronsUpDown aria-hidden className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
              <Command>
                <CommandInput placeholder={t("hazard.catalog.searchPlaceholder")} />
                <CommandList>
                  <CommandEmpty>{t("hazard.catalog.noMatches")}</CommandEmpty>
                  <CommandGroup>
                    {options.map((hazard: any) => {
                      const isSelected = selected.includes(hazard.code);
                      return (
                        <CommandItem
                          key={hazard.code}
                          value={`${hazard.code} ${hazard.label}`}
                          className="gap-2"
                          onSelect={() =>
                            setSelected(
                              isSelected
                                ? selected.filter((code) => code !== hazard.code)
                                : [...selected, hazard.code],
                            )
                          }
                        >
                          <Checkbox
                            checked={isSelected}
                            tabIndex={-1}
                            className="pointer-events-none"
                          />
                          <span className="min-w-0 break-words">
                            {`${hazard.code} · ${hazard.label} (${damageTypeLabel(t, hazard.damageType)})`}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <Button
          variant="outline"
          className="gap-2 max-sm:w-full"
          disabled={!dirty || saving}
          onClick={save}
        >
          {saving ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
          {t("operatorResponsibilityPanel.saveHazardAssignment")}
        </Button>
      </div>

      <div className="mt-4 flex min-w-0 flex-wrap items-center gap-1">
        {selected.length === 0 ? (
          // Not a neutral empty state: this is the gap the counter tracks.
          <span className="text-sm font-semibold text-destructive">
            {t("operatorResponsibilityPanel.noHazardAssigned")}
          </span>
        ) : (
          selected.map((code) => {
            const hazard = byCode.get(code);
            return (
              <HazardChip
                key={code}
                label={`${code} · ${hazard?.label || code}`}
                damageType={hazard?.damageType}
                relation="protects"
                t={t}
              />
            );
          })
        )}
      </div>
    </Section>
  );
}
